import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { showMessage } from "../messages/messagesActions";
import { IGroupedMarketing, IMarketings, IMarketingStatusCount } from "./marketing.types";
import { generateAdvanceFilterQuery } from "../Accounting/AccountingReports/AccountingReportsActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";

export const FETCH_MARKETINGS_HISTORY_LIST_PROGRESS =
  "FETCH_MARKETINGS_HISTORY_LIST_PROGRESS";
export const FETCH_MARKETINGS_HISTORY_LIST_SUCCESS =
  "FETCH_MARKETINGS_HISTORY_LIST_SUCCESS";
export const FETCH_MARKETINGS_HISTORY_LIST_FAILED =
  "FETCH_MARKETINGS_HISTORY_LIST_FAILED";

export const fetchMarketingsHistoryListProgress = () =>
  action(FETCH_MARKETINGS_HISTORY_LIST_PROGRESS);
export const fetchMarketingsHistoryListSuccess = (
  list: IGroupedMarketing[],
  totalRecords: number,
) => action(FETCH_MARKETINGS_HISTORY_LIST_SUCCESS, { list, totalRecords });
export const fetchMarketingsHistoryListFailed = () =>
  action(FETCH_MARKETINGS_HISTORY_LIST_FAILED);

const execGroupData = (data: IMarketings[]): IGroupedMarketing[] => {
  // Use a Map instead of a plain object so insertion order is preserved.
  // Plain objects reorder integer-like keys (e.g. customer_policy_id) into
  // ascending numeric order, which would break the sort order returned by the API.
  const groupedMap = new Map<IMarketings["customer_policy_id"], IGroupedMarketing>();
  data.forEach((obj) => {
    const existing = groupedMap.get(obj.customer_policy_id);
    if (!existing) {
      groupedMap.set(obj.customer_policy_id, {
        id: obj.customer_policy_id,
        ...obj,
        childs: [obj],
      } as IGroupedMarketing);
    } else {
      existing.childs.push(obj);
    }
  });
  return Array.from(groupedMap.values());
};

export const fetchMarketingsHistoryListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    searchState: { [key: string]: any },
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { from_date, to_date, customer_policy_id, ...finalSearchState } =
        searchState;
      // Remove empty arrays and empty strings from search state
      const cleanedSearchState = Object.fromEntries(
        Object.entries(finalSearchState).filter(([_, v]) => {
          if (Array.isArray(v)) return v.length > 0;
          return v !== "" && v !== null && v !== undefined;
        }),
      );
      const query = generateAdvanceFilterQuery(cleanedSearchState);
      dispatch(fetchMarketingsHistoryListProgress());
      let url = "";
      if (status !== "-1") {
        url = "&quote_status=" + status;
      }
      let finalUrl = `/tasks/get-all-policy-marketing-record?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}${url}`;
      if (from_date) {
        finalUrl = finalUrl + "&from_date=" + from_date;
      }
      if (to_date) {
        finalUrl = finalUrl + "&to_date=" + to_date;
      }
      if (query) {
        finalUrl = finalUrl + "&" + query;
      }
      const res = await api.get(finalUrl);

      // The history endpoint returns flat records (one row per marketing
      // history entry), so we must NOT collapse them by customer_policy_id.
      // Grouping here hid rows that shared a policy id and broke pagination
      // (fewer visible rows than the page size disabled the Next button).
      const data: IMarketings[] = res.data.data || [];

      const totalRecords = res.data.totalRecords;

      dispatch(fetchMarketingsHistoryListSuccess(data as any, totalRecords));
    } catch (err: any) {
      dispatch(fetchMarketingsHistoryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_MARKETINGS_GROUPED_LIST_PROGRESS =
  "FETCH_MARKETINGS_GROUPED_LIST_PROGRESS";
export const FETCH_MARKETINGS_GROUPED_LIST_SUCCESS =
  "FETCH_MARKETINGS_GROUPED_LIST_SUCCESS";
export const FETCH_MARKETINGS_GROUPED_LIST_FAILED =
  "FETCH_MARKETINGS_GROUPED_LIST_FAILED";

export const fetchMarketingsGroupedListProgress = () =>
  action(FETCH_MARKETINGS_GROUPED_LIST_PROGRESS);
export const fetchMarketingsGroupedListSuccess = (list: IGroupedMarketing[]) =>
  action(FETCH_MARKETINGS_GROUPED_LIST_SUCCESS, { list });
export const fetchMarketingsGroupedListFailed = () =>
  action(FETCH_MARKETINGS_GROUPED_LIST_FAILED);

export const fetchMarketingsGroupedListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    date: IDataTableV2DateState["dates"],
    searchState: { [key: string]: any } = {},
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchMarketingsGroupedListProgress());
      // Pass filters (incl. the selected quote_status) through the advanceFilter
      // query. Strip empty arrays/strings and the "-1" (All) sentinel first.
      const cleanedSearchState = Object.fromEntries(
        Object.entries(searchState || {}).filter(([_, v]) => {
          if (Array.isArray(v)) return v.length > 0;
          return v !== "" && v !== null && v !== undefined && v !== "-1";
        }),
      );
      const query = generateAdvanceFilterQuery(cleanedSearchState);

      let finalUrl = `/tasks/get-all-policy-marketing?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (query) {
        finalUrl = finalUrl + "&" + query;
      }

      const res = await api.get(finalUrl);

      const data: IGroupedMarketing[] = res.data.data;
      const finalData = execGroupData(data);

      dispatch(fetchMarketingsGroupedListSuccess(finalData));
    } catch (err: any) {
      dispatch(fetchMarketingsGroupedListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_MARKETING_STATUS_COUNT_PROGRESS =
  "FETCH_MARKETING_STATUS_COUNT_PROGRESS";
export const FETCH_MARKETING_STATUS_COUNT_SUCCESS =
  "FETCH_MARKETING_STATUS_COUNT_SUCCESS";
export const FETCH_MARKETING_STATUS_COUNT_FAILED =
  "FETCH_MARKETING_STATUS_COUNT_FAILED";

export const fetchMarketingStatusCountProgress = () =>
  action(FETCH_MARKETING_STATUS_COUNT_PROGRESS);
export const fetchMarketingStatusCountSuccess = (
  list: IMarketingStatusCount[],
) => action(FETCH_MARKETING_STATUS_COUNT_SUCCESS, { list });
export const fetchMarketingStatusCountFailed = () =>
  action(FETCH_MARKETING_STATUS_COUNT_FAILED);

export const fetchMarketingStatusCountAsync =
  (): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchMarketingStatusCountProgress());
      const res = await api.get(`/analytics/get-add-marketing-status-count`);
      const data: IMarketingStatusCount[] = res.data.data || [];
      dispatch(fetchMarketingStatusCountSuccess(data));
    } catch (err: any) {
      dispatch(fetchMarketingStatusCountFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
